/**
 * Component validator function type
 */
export type ComponentValidator<T = unknown> = (instance: T) => void | never;
import { logger } from '@/utils/logger.js';
import { useTokchanStore } from '@/store/tokchanStore.js';

/**
 * Component lifecycle hook type
 */
export type ComponentLifecycleHook<T = unknown> = (instance: T) => void | Promise<void>;

/**
 * Component factory function type
 */
export type ComponentFactory<T = unknown> = () => T | Promise<T>;

/**
 * Component initialization function type
 */
export type ComponentInitFunction<T = unknown> = () => T | Promise<T>;

/**
 * Component class constructor type
 */
export type ComponentClass<T = unknown> = new (...args: unknown[]) => T;

/**
 * Component definition configuration
 */
export interface ComponentDefinition<T = unknown> {
    /** Component name */
    name?: string;
    /** Component class constructor */
    class?: ComponentClass<T>;
    /** Factory function to create component instance */
    factory?: ComponentFactory<T>;
    /** Initialization function */
    init?: ComponentInitFunction<T>;
    /** Global window property name */
    global?: string;
    /** ManagerRegistry registration key */
    register?: string;
    /** After initialization hook */
    afterInit?: ComponentLifecycleHook<T>;
    /** Before initialization hook */
    beforeInit?: ComponentLifecycleHook<T>;
    /** Component validators */
    validators?: ComponentValidator<T>[];
    /** Component dependencies */
    dependencies?: string[];
    /** Custom component flag */
    custom?: boolean;
    /** Registration timestamp */
    registeredAt?: number;
}

/**
 * Internal component definition with name
 */
interface InternalComponentDefinition<T = unknown> extends ComponentDefinition<T> {
    name: string;
}

/**
 * Component initialization result
 */
export interface ComponentInitResult {
    initialized: boolean;
}

/**
 * Custom component registration configuration
 */
export interface CustomComponentConfig<T = unknown> extends ComponentDefinition<T> {
    custom?: boolean;
    registeredAt?: number;
}

/**
 * VideoSystemConfig interface
 */
export interface IVideoSystemConfig {
    loadConfig: () => Promise<unknown>;
    config?: unknown;
    get?: (key: string) => unknown;
}

/**
 * DebugConsole interface
 */
interface IDebugConsole {
    log: (...args: unknown[]) => void;
    error: (...args: unknown[]) => void;
}

/**
 * ManagerRegistry interface
 */
interface IManagerRegistry {
    register: (key: string, instance: unknown) => void;
    get: (key: string) => unknown;
    notifyReactHooks?: () => void;
}

/**
 * MediaPathRegistry interface
 * window.d.tsのMediaPathRegistry型と同期
 */
interface IMediaPathRegistry {
    loadStatus?: Record<string, boolean>;
    initializeRegistry?: () => Promise<void>;
    getAllBGMTracks?: () => Array<{
        id: string;
        title?: string;
        name?: string;
        file?: string;
        duration?: number;
        [key: string]: unknown;
    }>;
    getBGMPath?: (id: string) => string;
    getAllGifts?: () => Array<{
        id: string | number;
        file?: string;
        [key: string]: unknown;
    }>;
    getGiftPath?: (id: string | number) => string;
    getEffectPath?: (effectName: string) => Promise<string | undefined> | string;
    getTransitionPath?: (id: number) => string;
    getAllBackgrounds?: (category?: string) => Array<{
        id: string;
        file?: string;
        [key: string]: unknown;
    }>;
    [key: string]: unknown;
}

/**
 * EnhancedMediaPathRegistry interface
 */
interface IEnhancedMediaPathRegistry {
    unifiedCatalog?: unknown;
    initializeEnhancedRegistry: () => Promise<void>;
}

// Window types are defined in global.d.ts

/**
 * コンポーネントレジストリ
 * 各コンポーネントの初期化方法を定義
 */
class ComponentRegistry {
    private static definitions = new Map<string, InternalComponentDefinition>();

    /**
     * コンポーネント定義の登録
     */
    static register<T = unknown>(name: string, definition: ComponentDefinition<T>): void {
        this.definitions.set(name, {
            name,
            ...definition
        } as InternalComponentDefinition);
    }

    /**
     * 定義の取得
     */
    static get<T = unknown>(name: string): InternalComponentDefinition<T> | undefined {
        return this.definitions.get(name) as InternalComponentDefinition<T> | undefined;
    }

    /**
     * 全定義の取得
     */
    static getAll(): Map<string, InternalComponentDefinition> {
        return new Map(this.definitions);
    }

    /**
     * 定義の存在確認
     */
    static has(name: string): boolean {
        return this.definitions.has(name);
    }

    /**
     * 定義の削除
     */
    static unregister(name: string): boolean {
        const deleted = this.definitions.delete(name);
        if (deleted) {
            // Component unregistered successfully
        }
        return deleted;
    }

    /**
     * 標準コンポーネントの登録
     */
    static registerStandardComponents(): void {

        // VideoSystemConfig
        this.register<IVideoSystemConfig>('VideoSystemConfig', {
            class: window.VideoSystemConfig as ComponentClass<IVideoSystemConfig>,
            global: 'videoConfig',
            register: 'config',
            afterInit: async (instance) => {
                await instance.loadConfig();
            },
            validators: [(instance) => {
                if (!instance.config || !instance.get) {
                    throw new Error('Invalid VideoSystemConfig instance');
                }
            }]
        });

        // DebugConsole
        this.register<IDebugConsole>('DebugConsole', {
            factory: async () => {
                // 既にグローバルに存在する場合はそれを使用
                if (window.DebugConsole) {
                    return window.DebugConsole;
                }

                // DebugConsoleがグローバルになければエラー
                throw new Error('DebugConsole not available globally');
            },
            global: 'DebugConsole',
            register: 'debug',
            validators: [(instance: IDebugConsole) => {
                if (!instance.log || !instance.error) {
                    throw new Error('Invalid DebugConsole instance');
                }
            }]
        });

        // ManagerRegistry
        this.register<IManagerRegistry>('ManagerRegistry', {
            factory: async () => {
                // Zustand storeから取得を優先
                const storeRegistry = useTokchanStore.getState().getManagerRegistry();
                if (storeRegistry) {
                    return storeRegistry as IManagerRegistry;
                }
                // レガシーフォールバック
                if (window.ManagerRegistry) {
                    return window.ManagerRegistry;
                }

                // ManagerRegistryが利用不可の場合はエラー
                throw new Error('ManagerRegistry not available');
            },
            global: 'ManagerRegistry',
            validators: [(instance: IManagerRegistry) => {
                if (!instance.register || !instance.get) {
                    throw new Error('Invalid ManagerRegistry instance');
                }
            }]
        });

        // MediaPathRegistry
        this.register<IMediaPathRegistry>('MediaPathRegistry', {
            factory: async () => {
                // 既にグローバルに存在する場合はそれを使用
                if (window.MediaPathRegistry) {
                    return window.MediaPathRegistry;
                }

                // MediaPathRegistryがグローバルになければエラー
                throw new Error('MediaPathRegistry not available globally');
            },
            global: 'MediaPathRegistry',
            register: 'mediapath',
            afterInit: async (instance: IMediaPathRegistry) => {
                if (instance.initializeRegistry) {
                    await instance.initializeRegistry();
                }
            },
            validators: [(instance: IMediaPathRegistry) => {
                if (!instance.loadStatus) {
                    throw new Error('MediaPathRegistry initialization failed');
                }
            }]
        });

        // EnhancedMediaPathRegistry
        this.register<IEnhancedMediaPathRegistry>('EnhancedMediaPathRegistry', {
            init: async () => {
                // Zustand store経由で取得を優先
                const storeInstance = useTokchanStore.getState().getEnhancedMediaPathRegistry();
                if (storeInstance) {
                    return storeInstance as unknown as IEnhancedMediaPathRegistry;
                }

                // MediaPathRegistryの初期化を確認
                const mediaRegistry = useTokchanStore.getState().getMediaPathRegistry();
                if (!mediaRegistry && !window.MediaPathRegistry) {
                    throw new Error('MediaPathRegistry is required for EnhancedMediaPathRegistry');
                }

                // EnhancedMediaPathRegistryクラスがグローバルに存在することを確認
                if (!window.EnhancedMediaPathRegistry) {
                    throw new Error('EnhancedMediaPathRegistry class not available globally');
                }

                // インスタンスを作成
                const RegistryClass = window.EnhancedMediaPathRegistry as new () => IEnhancedMediaPathRegistry;
                const instance = new RegistryClass();
                await instance.initializeEnhancedRegistry();

                // Zustand storeに登録（型アサーションで互換性確保）
                useTokchanStore.getState().setEnhancedMediaPathRegistry(instance as unknown as import('@/store/tokchanStore.js').EnhancedMediaPathRegistryInstance);

                return instance;
            },
            global: 'enhancedMediaPathRegistry',
            register: 'enhanced-media',
            dependencies: ['MediaPathRegistry'],
            validators: [(instance: IEnhancedMediaPathRegistry) => {
                if (!instance.unifiedCatalog) {
                    throw new Error('EnhancedMediaPathRegistry unified catalog not loaded');
                }
            }]
        });

        // SimpleGiftSystem (旧GiftVideoManagerの置き換え)
        this.register('SimpleGiftSystem', {
            factory: async () => {
                // 既存インスタンスを優先
                if (window.simpleGiftSystem) {
                    return window.simpleGiftSystem;
                }

                // SimpleGiftSystemをグローバルから取得
                if (!window.SimpleGiftSystem) {
                    throw new Error('SimpleGiftSystem class not available globally');
                }

                const instance = new window.SimpleGiftSystem();

                // SimpleGiftSystemは自動初期化されるため、待機は不要
                // しかし、念のため初期化完了を待つ
                const instanceWithInit = instance as { initialize?: () => Promise<void> };
                if (instanceWithInit.initialize && typeof instanceWithInit.initialize === 'function') {
                    await instanceWithInit.initialize();
                }

                return instance;
            },
            global: 'simpleGiftSystem',
            register: 'gift',
            dependencies: [] // SimpleGiftSystemは独立動作
        });

        // UnifiedBackgroundSystem (統一背景システム - レガシーRandomBackgroundManagerの置き換え)
        this.register('RandomBackgroundManager', {
            factory: async () => {
                // UnifiedBackgroundSystemを優先（Zustand store経由）
                const existingUnifiedBg = useTokchanStore.getState().getUnifiedBackgroundSystem();
                if (existingUnifiedBg) {
                    return existingUnifiedBg;
                }

                // レガシーRandomBackgroundManagerのフォールバック
                const existingBgManager = useTokchanStore.getState().getRandomBackgroundManager();
                if (existingBgManager) {
                    logger.warn('⚠️ Using legacy RandomBackgroundManager (VideoSystemFacade)');
                    return existingBgManager;
                }

                // UnifiedBackgroundSystemを初期化
                try {
                    const { default: UnifiedBackgroundSystem } = await import('../UnifiedBackgroundSystem.js');
                    const unifiedBgSystem = new UnifiedBackgroundSystem();

                    // コンテナが存在する場合のみ初期化
                    const container = document.querySelector('.character-only-panel');
                    if (container) {
                        await unifiedBgSystem.initialize('.character-only-panel');
                        unifiedBgSystem.start();

                        // Zustand storeに登録
                        useTokchanStore.getState().setUnifiedBackgroundSystem(unifiedBgSystem as unknown as Parameters<ReturnType<typeof useTokchanStore.getState>['setUnifiedBackgroundSystem']>[0]);

                        logger.info('✅ UnifiedBackgroundSystem initialized via ComponentRegistry');
                        return unifiedBgSystem;
                    } else {
                        throw new Error('Container .character-only-panel not found');
                    }
                } catch (error) {
                    logger.warn('⚠️ UnifiedBackgroundSystem initialization failed, falling back to VideoSystemFacade:', error);

                    // フォールバック: VideoSystemFacadeを使用
                    const { VideoSystemFacade } = await import('../video-system/unified/index.js');
                    const facade = VideoSystemFacade.getInstance();
                    await facade.initialize();

                    // Zustand storeに登録
                    useTokchanStore.getState().setRandomBackgroundManager(facade as unknown as Parameters<ReturnType<typeof useTokchanStore.getState>['setRandomBackgroundManager']>[0]);
                    return facade;
                }
            },
            global: 'randomBackgroundManager',
            register: 'background',
            dependencies: ['EnhancedMediaPathRegistry']
        });

        // TransitionManager
        this.register('TransitionManager', {
            factory: async () => {
                // 既存インスタンスを優先（Zustand store経由）
                const existingInstance = useTokchanStore.getState().getTransitionManager();
                if (existingInstance) {
                    return existingInstance;
                }

                // TransitionManagerをグローバルから取得
                if (!window.TransitionManager) {
                    throw new Error('TransitionManager class not available globally');
                }

                const instance = new window.TransitionManager();

                // Zustand storeに登録
                useTokchanStore.getState().setTransitionManager(instance);

                return instance;
            },
            global: 'transitionManager',
            register: 'transition'
        });

        // React App
        this.register<ComponentInitResult>('ReactApp', {
            init: async (): Promise<ComponentInitResult> => {
                // DOM完了とReact準備の待機
                return new Promise<ComponentInitResult>((resolve, reject) => {
                    const timeout: NodeJS.Timeout = setTimeout(() => {
                        reject(new Error('React initialization timeout'));
                    }, 15000);

                    const checkReady = (): void => {
                        if (document.readyState === 'complete') {
                            clearTimeout(timeout);
                            resolve({ initialized: true });
                        } else {
                            setTimeout(checkReady, 100);
                        }
                    };

                    if (document.readyState === 'complete') {
                        clearTimeout(timeout);
                        resolve({ initialized: true });
                    } else {
                        window.addEventListener('load', () => {
                            clearTimeout(timeout);
                            resolve({ initialized: true });
                        });
                    }
                });
            },
            afterInit: (): void => {
                // React Hookへの通知（Zustand store経由）
                const registry = useTokchanStore.getState().getManagerRegistry() as { forceNotifyReactHooks?: () => void } | null;
                if (registry?.forceNotifyReactHooks) {
                    registry.forceNotifyReactHooks();
                }

                // カスタムイベントの発行
                window.dispatchEvent(new CustomEvent('react-app-ready', {
                    detail: { timestamp: Date.now() }
                }));

            }
        });

    }

    /**
     * カスタムコンポーネント登録用のヘルパー
     */
    static registerCustomComponent<T = unknown>(name: string, config: CustomComponentConfig<T>): void {
        const customConfig: ComponentDefinition<T> = {
            ...config,
            custom: true,
            registeredAt: Date.now()
        };

        this.register(name, customConfig);
    }

    /**
     * 依存関係の検証
     */
    static validateDependencies(): boolean {
        const errors: string[] = [];

        Array.from(this.definitions.entries()).forEach(([name, definition]) => {
            if (definition.dependencies) {
                for (const dep of definition.dependencies) {
                    if (!this.definitions.has(dep)) {
                        errors.push(`${name} depends on undefined component: ${dep}`);
                    }
                }
            }
        });

        if (errors.length > 0) {
            throw new Error(`Dependency validation failed:\n${errors.join('\n')}`);
        }

        return true;
    }

    /**
     * 登録状況の表示
     */
    static showStatus(): void {
        console.group('📋 Component Registry Status');

        Array.from(this.definitions.entries()).forEach(([_name, definition]) => {
            const _deps = definition.dependencies ? ` (deps: ${definition.dependencies.join(', ')})` : '';
            const _custom = definition.custom ? ' [CUSTOM]' : '';
        });

        console.groupEnd();
    }
}

// デバッグ用API
if (typeof window !== 'undefined') {
    window.showComponents = () => ComponentRegistry.showStatus();
    window.validateComponents = () => ComponentRegistry.validateDependencies();
}

export default ComponentRegistry;
